import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Plus, Play, Pause, Pencil, Trash2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useRecurringPayments, useUpdateRecurringPayment, useDeleteRecurringPayment } from '@/hooks/finance/useRecurringPayments';
import { RecurringPayment } from '@/types/database/recurring-payments';
import NewRecurringPaymentDialog from '@/components/finance/NewRecurringPaymentDialog';
import { format, isPast, isToday } from 'date-fns';

export default function RecurringPaymentsPage() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { data: payments, isLoading } = useRecurringPayments();
  const updateMutation = useUpdateRecurringPayment();
  const deleteMutation = useDeleteRecurringPayment();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<RecurringPayment | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleEdit = (p: RecurringPayment) => {
    setEditingPayment(p);
    setDialogOpen(true);
  };

  const handleToggleActive = (p: RecurringPayment) => {
    updateMutation.mutate({ id: p.id, is_active: !p.is_active });
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const getDueStatus = (dateStr: string, isActive: boolean) => {
    if (!isActive) return <Badge variant="secondary">Paused</Badge>;
    const date = new Date(dateStr);
    if (isToday(date)) return <Badge variant="secondary" className="border-orange-300 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">Due Today</Badge>;
    if (isPast(date)) return <Badge variant="destructive">Overdue</Badge>;
    return <Badge variant="outline">Upcoming</Badge>;
  };

  const monthlyByCurrency = payments
    ?.filter(p => p.is_active)
    .reduce((acc, p) => {
      const multiplier = { weekly: 4.33, monthly: 1, quarterly: 1 / 3, yearly: 1 / 12 }[p.frequency] || 1;
      const currency = p.currency || 'ETB';
      acc[currency] = (acc[currency] || 0) + p.amount * multiplier;
      return acc;
    }, {} as Record<string, number>) || {};

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/finance')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Recurring Payments</h1>
              <p className="text-muted-foreground">Track recurring obligations for alerts & cashflow forecasting</p>
            </div>
          </div>
          <Button onClick={() => { setEditingPayment(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Payment
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Active</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold">{payments?.filter(p => p.is_active).length || 0}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Est. Monthly Cost</CardTitle></CardHeader>
            <CardContent>
              {Object.keys(monthlyByCurrency).length === 0 ? (
                <div className="text-2xl font-bold">0.00 ETB</div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(monthlyByCurrency).map(([currency, total]) => (
                    <div key={currency} className="text-2xl font-bold">
                      {total.toLocaleString('en', { minimumFractionDigits: 2 })} {currency}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Overdue</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {payments?.filter(p => p.is_active && isPast(new Date(p.next_due_date)) && !isToday(new Date(p.next_due_date))).length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : !payments?.length ? (
              <div className="p-8 text-center text-muted-foreground">
                No recurring payments yet. Click "Add Payment" to create one.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Next Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id} className={!p.is_active ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="capitalize">{p.payment_type}</TableCell>
                      <TableCell>{p.vendor_name || '-'}</TableCell>
                      <TableCell>{Number(p.amount).toLocaleString('en', { minimumFractionDigits: 2 })} {p.currency}</TableCell>
                      <TableCell className="capitalize">{p.frequency}</TableCell>
                      <TableCell>{format(new Date(p.next_due_date), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{getDueStatus(p.next_due_date, p.is_active)}</TableCell>
                      <TableCell>{p.category?.name || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleToggleActive(p)} title={p.is_active ? 'Pause' : 'Resume'}>
                            {p.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {userRole === 'admin' && (
                            <Button variant="ghost" size="icon" onClick={() => setDeleteId(p.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <NewRecurringPaymentDialog open={dialogOpen} onOpenChange={setDialogOpen} payment={editingPayment} />

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Recurring Payment</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete this recurring payment. This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
